import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

function MiniChart({ data, color = '#c94a1e', height = 40 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const w = 120, h = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={color} opacity="0.1" />
    </svg>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const [txRes, agentsRes, alertsRes] = await Promise.all([
        api.get('/api/v1/transactions/?limit=200').catch(() => ({ data: [] })),
        api.get('/api/v1/agents/').catch(() => ({ data: [] })),
        api.get('/api/v1/compliance/alerts').catch(() => ({ data: [] })),
      ]);
      const txs = Array.isArray(txRes.data) ? txRes.data : [];
      const agents = Array.isArray(agentsRes.data) ? agentsRes.data : [];
      const alerts = Array.isArray(alertsRes.data) ? alertsRes.data : [];

      // Volume by day (last 7 days)
      const now = new Date();
      const volumeByDay = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(now);
        d.setDate(d.getDate() - (6 - i));
        const dayStr = d.toDateString();
        return txs
          .filter(t => t.status === 'completed' && new Date(t.created_at).toDateString() === dayStr)
          .reduce((s, t) => s + (t.amount_usd || 0), 0);
      });

      // TX by day
      const txByDay = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(now);
        d.setDate(d.getDate() - (6 - i));
        const dayStr = d.toDateString();
        return txs.filter(t => new Date(t.created_at).toDateString() === dayStr).length;
      });

      setStats({
        totalTx: txs.length,
        activeTx: txs.filter(t => ['in_delivery', 'queued_offline', 'pending_payment'].includes(t.status)).length,
        completedTx: txs.filter(t => t.status === 'completed').length,
        failedTx: txs.filter(t => t.status === 'failed').length,
        volume: txs.filter(t => t.status === 'completed').reduce((s, t) => s + (t.amount_usd || 0), 0),
        fees: txs.filter(t => t.status === 'completed').reduce((s, t) => s + (t.fee_usd || 0), 0),
        agentsOnline: agents.filter(a => a.is_online).length,
        agentsTotal: agents.length,
        pendingAlerts: alerts.filter(a => !a.resolved).length,
        queuedOffline: txs.filter(t => t.status === 'queued_offline').length,
        recentTx: txs.slice(0, 8),
        volumeByDay,
        txByDay,
        pendingAlertsList: alerts.filter(a => !a.resolved).slice(0, 3),
      });
      setLastRefresh(new Date());
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>;

  const cards = [
    { label: 'Volumen total', value: `$${(stats?.volume || 0).toFixed(0)}`, delta: `${stats?.completedTx || 0} completadas`, color: '#c94a1e', chart: stats?.volumeByDay },
    { label: 'TX activas', value: stats?.activeTx || 0, delta: `${stats?.queuedOffline || 0} en cola offline`, color: '#b45309', chart: stats?.txByDay },
    { label: 'Fees cobrados', value: `$${(stats?.fees || 0).toFixed(0)}`, delta: 'Ingresos Guarapo', color: '#2d6a4f', chart: stats?.volumeByDay },
    { label: 'Alertas pendientes', value: stats?.pendingAlerts || 0, delta: 'Compliance', color: '#9b2c5e', chart: null },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Panel de control</h1>
          <p style={{ fontSize: 13, color: '#7a7060', margin: 0 }}>Guarapo Services — Vista general</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <button onClick={load} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            ↻ Actualizar
          </button>
          <div style={{ fontSize: 11, color: '#b0a090', marginTop: 4 }}>
            Actualizado: {lastRefresh.toLocaleTimeString('es-ES')} · Auto-refresh 30s
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: '18px 20px', borderTop: `3px solid ${c.color}` }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 8 }}>{c.label}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: 32, lineHeight: 1, marginBottom: 5 }}>{c.value}</div>
                <div style={{ fontSize: 11.5, color: c.color, fontWeight: 600 }}>{c.delta}</div>
              </div>
              {c.chart && <MiniChart data={c.chart} color={c.color} />}
            </div>
          </div>
        ))}
      </div>

      {/* Agents status */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 24 }}>
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 15 }}>Red de agentes</div>
            <Link to="/app/admin/agentes" style={{ fontSize: 12, color: '#c94a1e', fontWeight: 600, textDecoration: 'none' }}>Ver todos →</Link>
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 36, color: '#2d6a4f' }}>{stats?.agentsOnline}</div>
              <div style={{ fontSize: 11, color: '#7a7060' }}>Online</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 36, color: '#7a7060' }}>{(stats?.agentsTotal || 0) - (stats?.agentsOnline || 0)}</div>
              <div style={{ fontSize: 11, color: '#7a7060' }}>Offline</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 36, color: '#b45309' }}>{stats?.queuedOffline}</div>
              <div style={{ fontSize: 11, color: '#7a7060' }}>En cola</div>
            </div>
          </div>
          {/* Agent bar */}
          <div style={{ marginTop: 16, background: '#f0ebe2', borderRadius: 6, height: 8, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#2d6a4f', width: `${stats?.agentsTotal ? (stats.agentsOnline / stats.agentsTotal * 100) : 0}%`, borderRadius: 6, transition: 'width .5s' }} />
          </div>
          <div style={{ fontSize: 11, color: '#7a7060', marginTop: 4 }}>
            {stats?.agentsTotal ? Math.round(stats.agentsOnline / stats.agentsTotal * 100) : 0}% de agentes online
          </div>
        </div>

        {/* Pending alerts */}
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 15 }}>Alertas compliance</div>
            <Link to="/app/admin/compliance" style={{ fontSize: 12, color: '#c94a1e', fontWeight: 600, textDecoration: 'none' }}>Ver todas →</Link>
          </div>
          {stats?.pendingAlerts === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', color: '#7a7060', fontSize: 13 }}>
              ✅ Sin alertas pendientes
            </div>
          ) : (
            stats?.pendingAlertsList?.map((alert, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: i < stats.pendingAlertsList.length - 1 ? '1px solid #f0ebe2' : 'none', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, color: '#9b2c5e' }}>{alert.alert_type}</span>
                  <span style={{ fontSize: 11, color: '#7a7060' }}>{alert.created_at ? new Date(alert.created_at).toLocaleDateString('es-ES') : '—'}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick links */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        {[
          { label: '📋 Órdenes', link: '/app/admin/txs', count: stats?.totalTx },
          { label: '🤝 Agentes', link: '/app/admin/agentes', count: `${stats?.agentsOnline || 0}/${stats?.agentsTotal || 0}` },
          { label: '👥 Usuarios', link: '/app/admin/usuarios', count: null },
          { label: '🔍 Compliance', link: '/app/admin/compliance', count: stats?.pendingAlerts },
        ].map(q => (
          <Link key={q.label} to={q.link} style={{ padding: '16px', background: '#fff', border: '1px solid #e2dbd0', borderRadius: 10, textAlign: 'center', textDecoration: 'none', color: '#0f0d0b', display: 'block' }}
            onMouseOver={e => e.currentTarget.style.background = '#f9f6f1'}
            onMouseOut={e => e.currentTarget.style.background = '#fff'}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{q.label}</div>
            {q.count !== null && q.count !== undefined && (
              <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: '#c94a1e', marginTop: 4 }}>{q.count}</div>
            )}
          </Link>
        ))}
      </div>

      {/* Recent TX */}
      {stats?.recentTx?.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '15px 20px', borderBottom: '1px solid #e2dbd0', display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 15 }}>Últimas órdenes</div>
            <Link to="/app/admin/txs" style={{ fontSize: 12, color: '#c94a1e', fontWeight: 600, textDecoration: 'none' }}>Ver todas →</Link>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{['Ref', 'Receptor', 'Monto', 'Fee', 'Estado', 'Fecha'].map(h => (
                <th key={h} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', padding: '10px 16px', background: '#faf8f5', borderBottom: '1px solid #e2dbd0', textAlign: 'left' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {stats.recentTx.map((tx, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0ebe2' }}>
                  <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 11, color: '#c94a1e' }}>{tx.ref}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>{tx.recipient_name}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 700 }}>${tx.amount_usd}</td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#7a7060' }}>${tx.fee_usd || 0}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: tx.status === 'completed' ? '#eaf4ef' : tx.status === 'failed' ? '#faeef4' : '#fef3e2', color: tx.status === 'completed' ? '#2d6a4f' : tx.status === 'failed' ? '#9b2c5e' : '#b45309' }}>
                      {tx.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#7a7060' }}>{tx.created_at ? new Date(tx.created_at).toLocaleDateString('es-ES') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
